import ReCAPTCHA from 'react-google-recaptcha';
import type { SubmitHandler, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { Col } from '@/components/Col';
import { Flex } from '@/components/Flex';
import { FormProvider } from '@/components/FormProvider';
import { InputField } from '@/components/InputField';
import { LegacyButton } from '@/components/LegacyButton';
import { CustomErrors } from '@/components/ServerErrors';
import { Typography } from '@/components/Typography';
import { GOOGLE_RECAPTCHA_PUBLIC_SITE_KEY } from '@/lib/const';
import { orderMessages } from '@/messages/order';

import * as S from '../OrderStatusView.styles';

interface FormValues {
  captcha: boolean;
  orderEmail: string;
  orderNumber: string;
  recaptchaToken: string | null;
}

interface OrderStatusFormProps {
  enableSubmit: boolean;
  error: string | null;
  formCtx: ReturnType<typeof useForm<FormValues>>;
  handleSubmit: SubmitHandler<FormValues>;
  isProcessing: boolean;
  onRecaptchaChange: (token: string | null) => void;
  onRecaptchaExpired: () => void;
  recaptchaKey: number;
}

export const OrderStatusForm = ({
  enableSubmit,
  error,
  formCtx,
  handleSubmit,
  isProcessing,
  onRecaptchaChange,
  onRecaptchaExpired,
  recaptchaKey,
}: OrderStatusFormProps) => {
  const { formatMessage } = useIntl();

  return (
    <S.Wrapper>
      <S.FormWrapper>
        <Typography textAlign='center'>
          {formatMessage(orderMessages.enterDetailsToCheckStatus)}
        </Typography>
        <FormProvider
          name='sign-up-my-account'
          onSubmit={handleSubmit}
          {...formCtx}
          data-test-id='SignUpViewFormProvider'
        >
          <S.Row gapDensity='none'>
            <Col>
              <InputField
                autoComplete='given-name'
                data-testid='OrderNumberInputField'
                id='OrderNumberInputField'
                name='orderNumber'
                placeholder={formatMessage(orderMessages.orderNumber)}
                required
              />
            </Col>
            <Col>
              <InputField
                autoComplete='additional-name'
                data-testid='OrderEmailInputField'
                id='OrderEmailInputField'
                name='orderEmail'
                placeholder={formatMessage(orderMessages.orderEmail)}
                required
              />
            </Col>
          </S.Row>
          <S.Row>
            <Col>
              <Flex
                alignItems='center'
                data-test-id='ReCaptchaFlex'
                flexDirection='column'
                justifyContent='center'
              >
                <ReCAPTCHA
                  key={recaptchaKey}
                  data-testid='ReCaptchaController'
                  onChange={onRecaptchaChange}
                  onExpired={onRecaptchaExpired}
                  sitekey={GOOGLE_RECAPTCHA_PUBLIC_SITE_KEY}
                />
              </Flex>
            </Col>
          </S.Row>
          <Col>
            <Flex justifyContent='center'>
              <LegacyButton
                data-testid='SignUpViewSubmitButton'
                disabled={!enableSubmit}
                isProcessing={isProcessing}
                label={formatMessage(orderMessages.checkStatus)}
                type='submit'
              />
            </Flex>
          </Col>
          {error && <CustomErrors message={error} />}
        </FormProvider>
      </S.FormWrapper>
    </S.Wrapper>
  );
};
